import { useEffect, useRef, useState } from "react";
import { Camera, Scan, Utensils, RefreshCw, Image as ImageIcon, Pencil, Sun, Move, Layers, X, Bookmark } from "lucide-react";
import { apiClient } from "../../services/api";
import TopBar from "../../components/layout/TopBar";
import BottomNav from "../../components/layout/BottomNav";
import GuestBanner from "../../components/common/GuestBanner";
import OfflineCard from "../../components/common/OfflineCard";
import RegistrationSheet from "../../components/common/RegistrationSheet";
import FoodScanResults, {
  FoodScanResultsSkeleton,
  FoodScanErrorCard,
} from "./FoodScanResults";
import ManualFoodEntry from "./ManualFoodEntry";
import PacketFlow from "./PacketFlow";
import { CounterLine, MealsStrip, SummaryCard } from "./GuestAccumulation";
import GuestSignupPrompt from "./GuestSignupPrompt";
import { useAuthStore } from "../../store/authStore";
import { isGuest, useGuestStore, trackGuest, setGuestMode } from "../../store/guestStore";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { compressImage } from "../../utils/imageCompress";
import { fireScanEvent } from "../../utils/compareFacts";

export default function FoodScanner() {
  const user = useAuthStore((s) => s.user);
  // Iter 51.2: auto-activate guest mode for any logged-out visitor on the
  // scanner-as-landing. Previously the PremiumOnboarding slider set this
  // flag; when we deleted the slider in Iter 50 the flag was never set,
  // which silently disabled the counter, meals strip, 6th-scan gate, and
  // all 429 upgrade prompts. Members skip this branch entirely.
  if (typeof window !== "undefined" && !user && !isGuest()) {
    setGuestMode(true);
  }
  const guest = !user; // sessionStorage flag is now guaranteed above
  const guestStore = useGuestStore();
  const inputRef = useRef(null);
  const galleryRef = useRef(null);
  const initialTab = (() => {
    try {
      const p = new URLSearchParams(window.location.search).get("mode");
      if (p === "gallery" || p === "barcode" || p === "scan" || p === "manual") return p;
    } catch { /* noop */ }
    return "scan";
  })();
  const [tab, setTab] = useState(initialTab); // scan | barcode | gallery | manual
  const [busy, setBusy] = useState(false);
  const [scan, setScan] = useState(null);
  const [scannedImage, setScannedImage] = useState(null);
  const [recentMeals, setRecentMeals] = useState([]);
  const [savedMeals, setSavedMeals] = useState([]);
  const [showTipCard, setShowTipCard] = useState(() => {
    try { return !window.localStorage.getItem("fitfrik_scan_tips_seen"); } catch { return true; }
  });
  const [scanError, setScanError] = useState(null);
  const [scanErrorReason, setScanErrorReason] = useState("");
  // Iter 52: Packet tab now runs a LABEL OCR primary flow. Once a barcode
  // is detected we silently try OFF; on miss we flip to labelMode so the
  // helper text and aim guide shift the user toward the nutrition panel.
  const [labelMode, setLabelMode] = useState(false);
  // Session 2B — wrong-tab safety net: {target: 'packet'|'food', b64}
  const [wrongMode, setWrongMode] = useState(null);
  // Photo handed to PacketFlow after a meal→packet switch (same photo, no recapture)
  const [externalCapture, setExternalCapture] = useState(null);
  const [offline, setOffline] = useState(typeof navigator !== "undefined" && !navigator.onLine);
  const [showReg, setShowReg] = useState(false);
  const [regContext, setRegContext] = useState("save_scan");
  // Iter 51: server-side quota (needed to detect the localStorage-cleared
  // case where server_count > local pendingFoodScans.length → softer variant).
  const [serverQuota, setServerQuota] = useState({
    used: 0,
    cap: 5,
    remaining: 5,
    daily_budget: 500,
    daily_used: 0,
    budget_reached: false,
    loaded: false,
  });
  const [showSignupGate, setShowSignupGate] = useState(false);
  const initialMeal = (() => {
    try {
      return (new URLSearchParams(window.location.search).get("meal") || "").toLowerCase();
    } catch { return ""; }
  })();

  const refreshRecent = () => {
    if (!user) return;
    apiClient
      .get("/nutrition/recent-meals?limit=5")
      .then((r) => setRecentMeals(r.data || []))
      .catch(() => setRecentMeals([]));
  };

  const refreshSaved = () => {
    if (!user) return;
    apiClient
      .get("/nutrition/saved-meals")
      .then((r) => setSavedMeals(r.data?.meals || []))
      .catch(() => setSavedMeals([]));
  };

  useEffect(() => {
    refreshRecent();
    refreshSaved();
    // Iter 50: guest landing_view event once per mount for a logged-out visitor.
    if (guest) {
      try { trackGuest("landing_view", { path: window.location.pathname }); } catch { /* noop */ }
    }
    // Iter 51: fetch server-side quota so we know the *true* count for this
    // device (fingerprint + anon_id). Used to decide the 6th-scan variant.
    if (!user) {
      const aid = guestStore.ensureAnonId();
      apiClient
        .get(`/guest/quota?anon_id=${encodeURIComponent(aid)}`)
        .then((r) => setServerQuota({ ...r.data, loaded: true }))
        .catch(() => setServerQuota((q) => ({ ...q, loaded: true })));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Listen for online/offline transitions
  useEffect(() => {
    const onOff = () => setOffline(true);
    const onOn = () => setOffline(false);
    window.addEventListener("offline", onOff);
    window.addEventListener("online", onOn);
    return () => {
      window.removeEventListener("offline", onOff);
      window.removeEventListener("online", onOn);
    };
  }, []);

  const resetScan = () => {
    setScan(null);
    setScannedImage(null);
    setScanError(null);
    setScanErrorReason("");
    refreshSaved();
  };

  // "Saved Meals" — a user-chosen template (distinct from auto Recent
  // Meals). Reopens straight into the result screen, no camera/AI call.
  const openSavedMeal = (meal) => {
    const items = (meal.items || []).map((it, i) => ({
      ...it,
      client_scan_id: `saved_${meal.meal_id}_${i}_${Date.now()}`,
    }));
    setScannedImage(null);
    setScan({ scan_id: meal.meal_id, meal_name: meal.name, items });
  };

  const handleFile = async (file) => {
    if (!file) return;
    if (!navigator.onLine) {
      setOffline(true);
      trackGuest("offline_encountered", {
        screen: "/nutrition",
        scan_count_at_time: guestStore.scanCount,
      });
      return;
    }
    // Iter 51: hard-gate the 6th scan client-side. Server will 429 too, but
    // gating here saves a network round-trip and shows the summary screen.
    if (guest && !user && guestStore.scanCount >= 5) {
      trackGuest("signup_shown", { context: "6th_scan_gate", trigger: "client" });
      setShowSignupGate(true);
      return;
    }
    setBusy(true);
    setScanError(null);
    setScanErrorReason("");
    setScan(null);
    let b64 = null;
    let thumb = null;
    try {
      // Compress to ~1600px wide JPEG (quality 0.82) BEFORE upload —
      // typically cuts a 4-5MB iPhone photo to ~250-400KB → upload time drops
      // from 2-3s to <500ms on 4G.
      b64 = await compressImage(file, { maxDim: 1600, quality: 0.82, targetKB: 500 });
      // Iter 51: generate a tiny 200x200 thumbnail for the meals strip + 6th-
      // scan gate. Non-fatal if it fails; the strip will fall back to a
      // gradient placeholder.
      try {
        thumb = await compressImage(file, { maxDim: 200, quality: 0.7, targetKB: 30 });
      } catch { /* non-fatal */ }
    } catch {
      setScanError("generic");
      setScanErrorReason("Could not load image. Try again.");
      setBusy(false);
      return;
    }
    await scanMealFromB64(b64, thumb);
  };

  // Session 2B — the meal-scan call, callable with an already-captured photo
  // so the wrong-tab switch can reuse the SAME image without recapture.
  const scanMealFromB64 = async (b64, thumb = null) => {
    setBusy(true);
    setScanError(null);
    setScanErrorReason("");
    setScan(null);
    setWrongMode(null);
    setScannedImage(b64);
    try {
      try {
        const anon_id = guestStore.ensureAnonId();
        const { data } = await apiClient.post("/nutrition/scan-multi", {
          image_base64: b64,
          anon_id,
        });
        if (data?.wrong_mode === "packet") {
          // Wrong-tab safety net — not counted, offer a one-tap switch.
          trackGuest("wrong_mode_fired", { from: "scan", to: "packet" });
          setWrongMode({ target: "packet", b64 });
        } else if (data?.error === "image_quality_insufficient") {
          setScanError("image_quality_insufficient");
          setScanErrorReason(data.reason || "");
        } else if (data?.error === "scan_failed") {
          // Infrastructure failure — never blame the photo for it.
          setScanError("generic");
          setScanErrorReason(data.reason || "We couldn't analyse this right now. Please try again in a moment.");
        } else if (data?.error === "no_food_detected") {
          setScanError("no_food_detected");
        } else if (Array.isArray(data?.items) && data.items.length > 0) {
          // Iter 51: decorate items with client_scan_id BEFORE handing to
          // FoodScanResults, so multiplier changes can sync back to
          // guestStore via updateScanByClientId.
          const stamp = new Date().toISOString();
          const epoch = Date.now();
          const decorated = data.items.map((it, i) => ({
            ...it,
            client_scan_id: `${data.scan_id || "scan"}_${i}_${epoch}`,
          }));
          const decoratedScan = { ...data, items: decorated };
          setScan(decoratedScan);
          trackGuest("scan_completed", { food_identified: true });
          if (guest) {
            // Iter50/51: persist the full payload (baseline multiplier=1)
            // so accumulation UI renders without a migration. Multiplier
            // edits in FoodScanResults sync via updateScanByClientId.
            decorated.forEach((it) => guestStore.addScan({
              client_scan_id: it.client_scan_id,
              dish_name: it.name,
              calories: it.calories,
              protein_g: it.protein_g,
              carbs_g: it.carbs_g,
              fat_g: it.fats_g ?? it.fat_g ?? 0,
              portion_multiplier: 1.0,
              scanned_at: stamp,
              thumbnail_base64: thumb,
              source: "scan",
            }));
            // Iter 51: refresh server quota so the counter updates.
            apiClient
              .get(`/guest/quota?anon_id=${encodeURIComponent(anon_id)}`)
              .then((r) => setServerQuota({ ...r.data, loaded: true }))
              .catch(() => {});
          }
        } else {
          setScanError("generic");
          setScanErrorReason("Could not read this food. Try again.");
        }
      } catch (err) {
        if (!navigator.onLine || /network|fetch/i.test(err?.message || "")) {
          setOffline(true);
        } else if (err?.response?.status === 429) {
          // Iter 51: server-side lifetime cap or budget hit — open the full
          // signup gate. The `detail.error` string tells us which one.
          const kind = err?.response?.data?.detail?.error;
          if (kind === "guest_cap_reached" || kind === "daily_budget_reached") {
            trackGuest("signup_shown", { context: "6th_scan_gate", trigger: "server_429", kind });
            setShowSignupGate(true);
          } else {
            setRegContext("rate_limit");
            setShowReg(true);
          }
        } else {
          setScanError("generic");
          setScanErrorReason("Could not reach AI service. Try again.");
        }
      }
    } catch {
      setScanError("generic");
      setScanErrorReason("Could not load image. Try again.");
    } finally {
      setBusy(false);
    }
  };

  const handleBarcode = async (codeArg) => {
    const code = (codeArg ?? "").trim();
    if (!/^\d{8,14}$/.test(code)) {
      toast.error("Enter a valid 8–14 digit barcode");
      return;
    }
    if (!navigator.onLine) {
      setOffline(true);
      return;
    }
    setBusy(true);
    setScanError(null);
    setScanErrorReason("");
    try {
      const { data } = await apiClient.post("/nutrition/barcode", { barcode: code });
      if (!data?.found) {
        // Iter 52: SILENT OFF miss. Do NOT show "product not found" — the
        // audit put OFF coverage in India at ~10%, so this is the common
        // case. Nudge the user to snap the nutrition label instead.
        setLabelMode(true);
        toast("Snap the nutrition label to read the pack", { duration: 3500 });
        return;
      }
      const newItem = {
        name: data.name,
        portion_description: data.portion_description,
        portion_grams: data.portion_grams,
        calories: data.calories,
        protein_g: data.protein_g,
        carbs_g: data.carbs_g,
        fats_g: data.fats_g,
        fibre_g: data.fibre_g || 0,
        sugar_g: data.sugar_g || 0,
        sodium_mg: data.sodium_mg || 0,
        glycemic_index: data.glycemic_index ?? null,
        source: "barcode",
        confidence: "high",
        image_url: data.image_url,
      };
      setScan({
        meal_name: data.name,
        items: [newItem],
        totals: {
          calories: data.calories,
          protein_g: data.protein_g,
          carbs_g: data.carbs_g,
          fats_g: data.fats_g,
          fibre_g: data.fibre_g || 0,
          sugar_g: data.sugar_g || 0,
          sodium_mg: data.sodium_mg || 0,
          glycemic_load: 0,
        },
        confidence: "high",
      });
      setScannedImage(data.image_url || null);
      if (guest) guestStore.addScan(newItem);
    } catch (err) {
      if (err?.response?.status === 429) {
        setRegContext("rate_limit");
        setShowReg(true);
      } else {
        // Iter 52: even a network error on OFF should not present as a
        // "product not found" failure. Slide to label OCR quietly.
        setLabelMode(true);
        toast("Snap the nutrition label to read the pack", { duration: 3500 });
      }
    } finally {
      setBusy(false);
    }
  };

  // Iter 52: shutter tapped on the Packet tab → PacketFlow (Session 3 split)
  // owns the label OCR lifecycle. Barcode lookups still land here because
  // they render into the meal result view (setScan).

  const logAgain = async (meal) => {
    try {
      await apiClient.post("/nutrition/log", {
        name: meal.name,
        calories: meal.calories,
        protein_g: meal.protein_g,
        carbs_g: meal.carbs_g,
        fat_g: meal.fat_g,
        portion_multiplier: meal.portion_multiplier || 1,
        source: "reuse",
      });
      toast.success(`${meal.name} logged · +10 FitPoints`);
      refreshRecent();
    } catch {
      toast.error("Could not log meal");
    }
  };

  // Show camera viewfinder/tabs only when no scan/error/skeleton is active
  const showCameraUI = !scan && !busy && !scanError && !wrongMode;
  const showSkeleton = busy && !!scannedImage;
  const showResults = !!scan && !busy && !scanError;
  const showError = !!scanError;
  const showNudge = guest && guestStore.scanCount >= 3 && !!scan;

  return (
    <div className="app-page" data-testid="food-scanner">
      <TopBar
        title={user ? "Food Scanner" : null}
        back={user ? false : false /* guests land here; the wordmark is the anchor */}
        hideMenu={guest}
      />
      {/* Iter 51: guest-only tagline directly under the wordmark, matching the
          login-page brand identity ("India's fitness super-app"). Members
          see the app in-context so we skip this for authed users. */}
      {guest && (
        <div
          className="max-w-2xl mx-auto px-4 pt-1 pb-2"
          data-testid="guest-brand-tagline"
        >
          <p
            style={{
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              fontWeight: 500,
              fontSize: 12,
              color: "#6B6760",
              letterSpacing: "0.01em",
            }}
          >
            India&apos;s fitness super-app · Know Your Food
          </p>
        </div>
      )}
      <main
        className="max-w-2xl mx-auto px-4 pt-3"
        style={{ paddingBottom: scan ? 96 : guest ? 144 : 32 }}
      >
        {offline ? (
          <OfflineCard onReconnect={() => setOffline(false)} />
        ) : (
          <>
            {/* Iter 51: Guest accumulation UI — sits above the tabs.
                Counter appears from scan 1, strip from scan 2+, summary
                only after 5. Members do not see any of this. */}
            {guest && showCameraUI && guestStore.scanCount >= 1 && (
              <CounterLine
                used={Math.max(guestStore.scanCount, serverQuota.used || 0)}
                cap={5}
                budgetReached={serverQuota.budget_reached}
              />
            )}
            {guest && showCameraUI && guestStore.pendingFoodScans.length >= 2 &&
              guestStore.pendingFoodScans.length < 5 && (
              <MealsStrip scans={guestStore.pendingFoodScans} />
            )}
            {guest && showCameraUI && guestStore.pendingFoodScans.length >= 5 && (
              <SummaryCard
                scans={guestStore.pendingFoodScans}
                onSignUp={() => {
                  trackGuest("summary_cta_clicked", { count: guestStore.pendingFoodScans.length });
                  setShowSignupGate(true);
                }}
              />
            )}

            {/* Tabs — camera view only */}
            {showCameraUI && (
              <div className="flex gap-1 p-1 bg-canvas2 rounded-pill mb-4" data-testid="scanner-tabs">
                {[
                  { id: "scan", label: "Scan Food", icon: Camera },
                  { id: "barcode", label: "Packet", icon: Scan },
                  { id: "gallery", label: "Gallery", icon: ImageIcon },
                  { id: "manual", label: "Manual", icon: Pencil },
                ].map((t) => {
                  const on = tab === t.id;
                  return (
                    <button
                      key={t.id}
                      onClick={() => setTab(t.id)}
                      data-testid={`tab-${t.id}`}
                      className="flex-1 h-9 grid place-items-center rounded-pill text-[12px] font-medium"
                      style={{
                        background: on ? "#FFFFFF" : "transparent",
                        color: on ? "#1A1A1A" : "#6B6760",
                        fontWeight: on ? 700 : 600,
                        border: on ? "1px solid #E2E0DC" : "1px solid transparent",
                        borderRadius: 100,
                      }}
                    >
                      <t.icon size={13} className="inline mr-1" />
                      {t.label}
                    </button>
                  );
                })}
              </div>
            )}

            {showCameraUI && tab === "scan" && showTipCard && (
              <div
                className="mb-3 rounded-[14px] p-4 relative"
                data-testid="scan-tips-card"
                style={{ background: "#FFFFFF", border: "1px solid #E2E0DC" }}
              >
                <button
                  onClick={() => {
                    setShowTipCard(false);
                    try { window.localStorage.setItem("fitfrik_scan_tips_seen", "1"); } catch { /* noop */ }
                  }}
                  aria-label="Dismiss tips"
                  data-testid="scan-tips-dismiss"
                  className="absolute top-3 right-3 w-6 h-6 rounded-full grid place-items-center"
                  style={{ background: "#ECEAE7" }}
                >
                  <X size={12} />
                </button>
                <div className="font-display text-[15px] font-bold mb-2">Tips for the best scan</div>
                <div className="space-y-1.5">
                  <TipLine icon={<Move size={13} />} text="Take the photo from directly above" />
                  <TipLine icon={<Sun size={13} />} text="Make sure lighting is good and even" />
                  <TipLine icon={<Layers size={13} />} text="Avoid overlapping items — spread the plate out" />
                </div>
              </div>
            )}

            {showCameraUI && tab === "scan" && (
              <div className="relative w-full aspect-[4/5] bg-black rounded-[20px] overflow-hidden border border-line">
                <img
                  src="https://images.unsplash.com/photo-1498837167922-ddd27525d352?w=1200&q=85&auto=format"
                  alt=""
                  className="absolute inset-0 w-full h-full object-cover opacity-40"
                />
                <Bracket pos="top-left" />
                <Bracket pos="top-right" />
                <Bracket pos="bottom-left" />
                <Bracket pos="bottom-right" />
                <div className="absolute top-6 left-0 right-0 text-center text-white px-6">
                  <div className="text-[14px] font-medium">Point at your meal</div>
                  <div className="text-[11px] mt-1" style={{ color: "rgba(255,255,255,0.75)" }}>
                    Shoot from top · good lighting · no overlap
                  </div>
                </div>
                <div className="absolute bottom-5 left-0 right-0 px-4 flex items-center justify-center">
                  <button
                    onClick={() => inputRef.current?.click()}
                    aria-label="Capture"
                    data-testid="capture-photo"
                    className="w-16 h-16 rounded-full bg-white grid place-items-center border-4 border-white/40"
                  >
                    <Camera size={22} className="text-black" />
                  </button>
                </div>
                <input
                  ref={inputRef}
                  type="file"
                  accept="image/*"
                  capture="environment"
                  className="hidden"
                  onChange={(e) => handleFile(e.target.files?.[0])}
                  data-testid="file-input"
                />
              </div>
            )}

            {showCameraUI && tab === "gallery" && (
              <div className="bg-white rounded-[16px] p-5 border border-line2" data-testid="gallery-tab">
                <div
                  className="w-full aspect-[4/3] rounded-[16px] border-2 border-dashed grid place-items-center cursor-pointer"
                  style={{ borderColor: "var(--line, #E2E0DC)", background: "var(--canvas2, #F5F3EE)" }}
                  onClick={() => galleryRef.current?.click()}
                  data-testid="gallery-drop"
                >
                  <div className="text-center px-6">
                    <div className="w-14 h-14 rounded-full mx-auto grid place-items-center" style={{ background: "rgba(15,181,166,0.15)" }}>
                      <ImageIcon size={26} color="#0FB5A6" />
                    </div>
                    <div className="mt-3 font-display text-[18px]">Upload from gallery</div>
                    <p className="text-[12px] text-ink-soft mt-1">
                      Pick a photo of your meal from your device. AI will identify the food & macros.
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => galleryRef.current?.click()}
                  data-testid="gallery-pick"
                  className="mt-4 w-full h-12 rounded-pill"
                  style={{ background: "var(--accent-blue)", color: "#000", borderRadius: 100, fontWeight: 600 }}
                >
                  Choose photo
                </button>
                <input
                  ref={galleryRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => handleFile(e.target.files?.[0])}
                  data-testid="gallery-input"
                />
              </div>
            )}

            {showCameraUI && tab === "barcode" && (
              <PacketFlow
                active={showCameraUI && tab === "barcode"}
                labelMode={labelMode}
                barcodeBusy={busy}
                externalCapture={externalCapture}
                onExternalConsumed={() => setExternalCapture(null)}
                onBarcode={(digits) => handleBarcode(digits)}
                onWrongMode={(dataUrl) => {
                  setWrongMode({ target: "food", b64: dataUrl });
                }}
                onRequireSignupGate={() => setShowSignupGate(true)}
                onRequireReg={(ctx) => { setRegContext(ctx); setShowReg(true); }}
                onOffline={() => setOffline(true)}
                trackGuest={trackGuest}
                refreshRecent={refreshRecent}
              />
            )}

            {showCameraUI && tab === "manual" && (
              <ManualFoodEntry
                initialMeal={initialMeal}
                onLogged={refreshRecent}
              />
            )}

            {/* AI Nutritionist link — camera view only, authed */}
            {showCameraUI && user && (
              <Link
                to="/nutrition/chat"
                data-testid="open-ai-chat"
                className="mt-5 flex items-center justify-between bg-white rounded-[16px] p-4 border border-line2"
                style={{ boxShadow: "var(--shadow-soft)" }}
              >
                <div>
                  <div className="font-display text-[18px] leading-tight">Ask the AI Nutritionist</div>
                  <div className="text-[12px] text-ink-soft mt-0.5">Indian-aware diet coach. Try a starter prompt.</div>
                </div>
                <span
                  className="px-4 h-9 grid place-items-center rounded-pill text-[12px] font-medium"
                  style={{ background: "var(--accent-blue-dim)", color: "var(--text-primary)", borderRadius: 100 }}
                >
                  Open chat
                </span>
              </Link>
            )}

            {/* Saved Meals — user-chosen templates, reopen with zero rescanning */}
            {showCameraUI && user && savedMeals.length > 0 && (
              <section className="mt-5" data-testid="saved-meals">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-display text-[18px]">Saved Meals</h3>
                </div>
                <div className="space-y-2">
                  {savedMeals.map((m, i) => (
                    <button
                      key={m.meal_id || i}
                      onClick={() => openSavedMeal(m)}
                      data-testid={`saved-meal-${i}`}
                      className="w-full bg-white rounded-[12px] p-3 border border-line2 flex items-center gap-3 text-left"
                    >
                      <Bookmark size={16} className="text-ink-muted shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-[14px] truncate">{m.name}</div>
                        <div className="text-[11px] text-ink-soft">
                          {(m.items || []).length} {(m.items || []).length === 1 ? "item" : "items"} · tap to open
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </section>
            )}

            {/* Recent Meals — show on camera screen only, now at the bottom */}
            {showCameraUI && user && recentMeals.length > 0 && (
              <section className="mt-5" data-testid="recent-meals">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-display text-[18px]">Recent Meals</h3>
                </div>
                <div className="space-y-2">
                  {recentMeals.map((m, i) => (
                    <div
                      key={i}
                      className="bg-white rounded-[12px] p-3 border border-line2 flex items-center gap-3"
                      data-testid={`recent-meal-${i}`}
                    >
                      <Utensils size={16} className="text-ink-muted shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-[14px] truncate">{m.name}</div>
                        <div className="text-[11px] text-ink-soft">
                          {m.last_eaten_date} · {Math.round(m.calories || 0)} kcal
                        </div>
                      </div>
                      <button
                        onClick={() => logAgain(m)}
                        data-testid={`log-again-${i}`}
                        className="px-3 h-8 rounded-pill text-[12px] font-medium flex items-center gap-1"
                        style={{ background: "var(--accent-blue)", color: "#000", borderRadius: 100 }}
                      >
                        <RefreshCw size={11} /> Log Again
                      </button>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Skeleton — while AI is identifying */}
            {showSkeleton && <FoodScanResultsSkeleton scannedImage={scannedImage} />}

            {/* Session 2B — wrong-tab safety net card. The scan was NOT
                counted; one tap re-runs the SAME photo through the right
                pipeline, no recapture. */}
            {wrongMode && (
              <div className="bg-white rounded-[16px] p-5 border border-line2" data-testid="wrong-mode-card">
                {(wrongMode.b64 || "").startsWith("data:") && (
                  <img src={wrongMode.b64} alt="" className="w-full mb-3 rounded-[12px]"
                       style={{ height: 160, objectFit: "cover" }} />
                )}
                <div className="font-display text-[18px]">
                  {wrongMode.target === "packet"
                    ? "Looks like a packet"
                    : "Looks like prepared food"}
                </div>
                <p className="text-[13px] mt-1" style={{ color: "#4A4845" }}>
                  {wrongMode.target === "packet"
                    ? "This photo looks like a packaged product. Switch to the Packet tab to read its label — we'll reuse this photo."
                    : "This photo looks like a plate of food. Switch to Scan Food to identify the dish — we'll reuse this photo."}
                  {" "}This attempt didn't use a scan.
                </p>
                <button
                  type="button"
                  data-testid="wrong-mode-switch"
                  onClick={() => {
                    const { target, b64 } = wrongMode;
                    setWrongMode(null);
                    trackGuest("wrong_mode_switched", { to: target });
                    fireScanEvent("wrong_mode_switched", { to: target });
                    if (target === "packet") {
                      setTab("barcode");
                      setExternalCapture(b64);
                    } else {
                      setTab("scan");
                      scanMealFromB64(b64);
                    }
                  }}
                  className="mt-3 w-full h-11 rounded-pill"
                  style={{ background: "#00D4FF", color: "#000", borderRadius: 100, fontWeight: 700 }}
                >
                  {wrongMode.target === "packet" ? "Switch to Packet tab" : "Switch to Scan Food"}
                </button>
                <button
                  type="button"
                  data-testid="wrong-mode-dismiss"
                  onClick={() => setWrongMode(null)}
                  className="mt-2 w-full h-10 text-[13px] font-medium"
                  style={{ color: "#4A4845" }}
                >
                  Retake instead
                </button>
              </div>
            )}

            {/* Error card — image quality / no food / generic */}
            {showError && (
              <div className="mt-2">
                <FoodScanErrorCard
                  error={scanError}
                  reason={scanErrorReason}
                  onRetake={resetScan}
                />
              </div>
            )}

            {/* Guest scan-count nudge — sits above the results */}
            {showNudge && (
              <div
                className="mb-3 rounded-[12px] p-3 text-[13px]"
                data-testid="scan-count-nudge"
                style={{
                  background: "rgba(0,212,255,0.12)",
                  border: "1px solid rgba(0,212,255,0.3)",
                  color: "var(--text-primary)",
                }}
              >
                You have scanned {guestStore.scanCount} foods. Save them all by{" "}
                <button
                  onClick={() => {
                    setRegContext("save_scan");
                    setShowReg(true);
                  }}
                  data-testid="nudge-create-account"
                  className="underline font-medium"
                  style={{ color: "#00B0D5" }}
                >
                  creating a free account
                </button>
                .
              </div>
            )}

            {/* New 9-section results screen */}
            {showResults && (
              <FoodScanResults
                scan={scan}
                scannedImage={scannedImage}
                onDiscard={resetScan}
                onSaved={() => {
                  refreshRecent();
                  refreshSaved();
                  setTimeout(resetScan, 400);
                }}
              />
            )}
          </>
        )}
      </main>

      {/* Bottom nav OR guest banner — always shown except when results render their own action bar */}
      {!scan && (
        guest ? (
          <GuestBanner
            onSignUp={() => {
              setRegContext("general");
              setShowReg(true);
            }}
          />
        ) : (
          <BottomNav />
        )
      )}

      {showReg && (
        <RegistrationSheet
          context={regContext}
          onSuccess={() => {
            setShowReg(false);
            // Stay on /nutrition; user is now authed
          }}
          onDismiss={() => setShowReg(false)}
        />
      )}

      {/* Iter 51: 6th-scan gate. Chooses variant based on whether server
          count exceeds what we still hold in localStorage. */}
      {showSignupGate && guest && (() => {
        const localCount = guestStore.pendingFoodScans.length;
        const serverCount = serverQuota.used || 0;
        // "softer" ONLY when server clearly has more than local — e.g. user
        // cleared storage between sessions. When server == local, we still
        // have their thumbnails, use the standard variant.
        const variant = serverQuota.loaded && serverCount > localCount ? "softer" : "standard";
        return (
          <GuestSignupPrompt
            variant={variant}
            scans={guestStore.pendingFoodScans}
            onDismiss={(r) => {
              setShowSignupGate(false);
              if (r?.success) {
                // Post-signup: RegistrationSheet already migrated + cleared
                // guest state. Nothing else to do here.
              }
            }}
          />
        );
      })()}
    </div>
  );
}

function TipLine({ icon, text }) {
  return (
    <div className="flex items-center gap-2">
      <span className="shrink-0 text-ink-tertiary">{icon}</span>
      <span className="text-[12px] text-ink-soft">{text}</span>
    </div>
  );
}

function Bracket({ pos }) {
  const map = {
    "top-left": "top-4 left-4 border-t-2 border-l-2 rounded-tl-md",
    "top-right": "top-4 right-4 border-t-2 border-r-2 rounded-tr-md",
    "bottom-left": "bottom-24 left-4 border-b-2 border-l-2 rounded-bl-md",
    "bottom-right": "bottom-24 right-4 border-b-2 border-r-2 rounded-br-md",
  };
  return (
    <div
      className={`absolute w-8 h-8 ${map[pos]}`}
      style={{ borderColor: "var(--accent-blue)" }}
    />
  );
}
